import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { getEmployeeDashboard } from '../services/aiEmployeeApi';
import UserStreamManager from '../utils/UserStreamManager';
import { markComputerOpen } from '../components/Chat/EmployeeComputer/computerOpenSource';

/**
 * EmployeeDashboardContext — single source of truth for the AI-employee dashboard, scoped to one
 * employee at a time. It exists ABOVE the right panel (mounted globally in App.js) so that when the
 * media viewer temporarily takes over the right panel (opening an artifact), the dashboard's data +
 * expansion state are NOT torn down — closing the artifact returns to the exact same view.
 *
 * Responsibilities (see agent_docs/employee_dashboard_single_pane_prd.md):
 *  - ONE aggregate fetch (getEmployeeDashboard) per employee, plus ONE gentle poll — replacing the old
 *    6-call fan-out + per-tab refetches + 6x getAiEmployee scattered across components.
 *  - Holds the single-pane EXPANSION state (which component is expanded in place) so the breadcrumb +
 *    view survive panel remounts and media takeovers.
 *  - Components READ employee/counts/previews from here instead of fetching themselves.
 */

const EmployeeDashboardContext = createContext(null);

export const useEmployeeDashboard = () => {
  const ctx = useContext(EmployeeDashboardContext);
  if (!ctx) {
    throw new Error('useEmployeeDashboard must be used within EmployeeDashboardProvider');
  }
  return ctx;
};

// WebSockets are the freshness engine; this is only a last-resort safety backstop against silent WS
// drift (much slower than the old blind 30s poll).
const POLL_MS = 180000;

// ---- Right-panel view preference ('hidden' | 'rail' | 'dashboard' | 'computer') ----
// The user's persisted default for what the employee chat's right slot shows — written ONLY by
// explicit clicks on the header's 3-way switch (transient drill-ins pass persist:false and never
// rewrite it). Global across employees for consistent muscle memory.
// See agent_docs/ai_employees_right_rail_panel_states_design.md.
const PANEL_PREF_KEY = 'cellcog_emp_panel_pref';
// EC/AC unification (Nitish 2026-08-26, agent_docs/ec_ac_unification_prd.md): the rail and
// dashboard panel states are RETIRED — the Employee Computer is the one right panel, toggled
// by a single header button (Agent Computer pattern). DEFAULT = OPEN ('computer', Nitish's
// call); after that the open/closed state persists here and applies across ALL employees
// (same muscle-memory model the old selector used). Legacy prefs ('rail'/'dashboard') read
// as the default.
const PANEL_VIEWS = ['hidden', 'computer'];
const readPanelPref = () => {
  try {
    const v = localStorage.getItem(PANEL_PREF_KEY);
    return PANEL_VIEWS.includes(v) ? v : 'computer';
  } catch {
    return 'computer';
  }
};

export const EmployeeDashboardProvider = ({ children }) => {
  // The employee currently shown in the right panel (set by the dashboard panel on mount).
  const [employeeId, setEmployeeId] = useState(null);
  // The aggregate payload: { employee, shift_state, counts, tasks_preview, dashboards, schedule, budget }.
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // Single-pane expansion: null = the multi-pane overview; otherwise the expanded component key
  // ('tasks' | 'settings' | 'dashboards' | ...). Survives media takeover.
  const [expandedComponent, setExpandedComponent] = useState(null);
  // Shift transition state machine (silky shift handoff): null | 'winding_down' | 'starting'.
  // Set by the header "Start new shift" button on click (per the API's action), advanced to 'starting'
  // by ChatPage when the shift/started WS event lands, cleared when the new shift's history loads.
  // Lives HERE (not in ChatPage) because both the header button and the chat surface read/drive it.
  // See agent_docs/ai_employees_shift_transition_silky_design.md.
  const [shiftTransition, setShiftTransition] = useState(null);
  // Right-panel view: 'hidden' | 'rail' | 'dashboard' | 'computer'. SESSION state initialized from the persisted
  // preference; explicit switch clicks persist, transient drill-ins don't (persist:false). At narrow
  // viewports the rail renders as a SUMMONED overlay/bottom-sheet gated by overlayOpen — never
  // auto-opened ("overlays are summoned, never sprung").
  const [panelView, setPanelViewState] = useState(readPanelPref);
  const [overlayOpen, setOverlayOpen] = useState(false);
  // Whether the rail currently FITS docked in the chat panel's blank right gutter (measured by
  // ChatPage's ResizeObserver). The header switch reads it to decide summon-vs-set and badge state.
  const [railDocked, setRailDocked] = useState(false);
  // Computer app INTENT (agent_docs/ec_home_app_prd.md §0.8): a deep link (ChatPage ?app=…) asks
  // the shell to open a specific app, optionally ON an item ({taskId|taskKey} / {threadId} /
  // {section}). The shell may not be mounted yet (panel closed), so the ask parks here and
  // EmployeeComputer consumes it once on mount/change, then clears it. {appId, intent, nonce}: the
  // nonce makes a repeated ask for the same app a fresh intent (focus/restore).
  const [computerIntent, setComputerIntent] = useState(null);
  const requestComputerApp = useCallback((appId, intent) => {
    markComputerOpen('deep_link');
    setComputerIntent({ appId, intent: intent || null, nonce: Date.now() });
  }, []);
  const clearComputerIntent = useCallback(() => setComputerIntent(null), []);

  const setPanelView = useCallback((view, { persist = true } = {}) => {
    if (!PANEL_VIEWS.includes(view)) return;
    setPanelViewState(view);
    if (persist) {
      try {
        localStorage.setItem(PANEL_PREF_KEY, view);
      } catch {
        /* storage unavailable (private mode) — session-only */
      }
    }
  }, []);

  const loadingRef = useRef(false);
  // A refresh requested WHILE one is already in-flight must not be silently dropped — otherwise a WS
  // event that lands mid-fetch (e.g. a dashboard `created` during the busy first-shift task burst) is
  // lost and the new board doesn't appear until a manual reload or the slow safety poll. We remember it
  // and run ONE trailing refresh when the in-flight one finishes (latest-state-wins, no pile-up).
  const pendingRef = useRef(false);
  const employeeIdRef = useRef(null);
  const debounceRef = useRef(null);
  useEffect(() => { employeeIdRef.current = employeeId; }, [employeeId]);

  const refresh = useCallback(async (id, { silent = false } = {}) => {
    const target = id || employeeIdRef.current;
    if (!target) return;
    if (loadingRef.current) {
      // Coalesce: mark that another refresh is owed and let the in-flight one trigger it on completion.
      pendingRef.current = true;
      return;
    }
    loadingRef.current = true;
    if (!silent) setLoading(true);
    try {
      const res = await getEmployeeDashboard(target);
      // Guard against a late response after the active employee changed.
      if (employeeIdRef.current === target) setData(res.data);
    } catch (e) {
      // non-fatal — keep the last good snapshot
      // eslint-disable-next-line no-console
      console.error('[EmployeeDashboard] aggregate load failed', e);
    } finally {
      loadingRef.current = false;
      if (!silent) setLoading(false);
      // A refresh was requested while we were loading → run exactly one trailing refresh now, so the
      // snapshot reflects the latest state (the dropped event self-heals immediately, not in 3 min).
      if (pendingRef.current) {
        pendingRef.current = false;
        refresh(employeeIdRef.current, { silent: true });
      }
    }
  }, []);

  // Coalesce a burst of WS events (a busy shift fires many) into ONE silent refresh.
  const scheduleRefresh = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      refresh(employeeIdRef.current, { silent: true });
    }, 500);
  }, [refresh]);

  // Set the active employee (called by ChatPage — the single owner). Resets expansion + data when the
  // employee actually changes; a no-op when it's the same employee (so the media takeover round-trip is
  // stable and doesn't reset the expanded view).
  const setActiveEmployee = useCallback((id) => {
    setEmployeeId((prev) => {
      if (prev === id) return prev;
      setData(null);
      setExpandedComponent(null);
      setShiftTransition(null);
      // A transient drill-in (persist:false) must not leak across employees — restore the pref.
      setPanelViewState(readPanelPref());
      setOverlayOpen(false);
      setComputerIntent(null);
      return id;
    });
  }, []);

  // Reconcile a specific section on drill-down: re-pull the golden snapshot once (cheap; the aggregate
  // is fast) so the expanded view is confirmed-fresh even if a WS event was missed. Exposed to the panel.
  const reconcileSection = useCallback(() => {
    refresh(employeeIdRef.current, { silent: true });
  }, [refresh]);

  // First fetch on employee change. The freshness ENGINE is WebSockets (below), not polling.
  useEffect(() => {
    if (!employeeId) return undefined;
    refresh(employeeId);
  }, [employeeId, refresh]);

  // WS-driven freshness: keep the golden snapshot live without polling.
  //  - EMPLOYEE_UPDATED (filtered to the active employee) → debounced silent refresh.
  //  - reconnect → reconcile once (covers events dropped while disconnected).
  //  - a SLOW safety poll (only while visible) as a last-resort backstop if WS silently drifts.
  useEffect(() => {
    if (!employeeId) return undefined;
    const unsub = UserStreamManager.subscribe('EMPLOYEE_UPDATED', (msg) => {
      if (msg && msg.employee_id === employeeIdRef.current) scheduleRefresh();
    });
    const unsubReconnect = UserStreamManager.addReconciliationCallback(() => {
      refresh(employeeIdRef.current, { silent: true });
    });
    const safetyPoll = setInterval(() => {
      if (document.visibilityState === 'visible') refresh(employeeIdRef.current, { silent: true });
    }, POLL_MS);
    return () => {
      unsub();
      unsubReconnect();
      clearInterval(safetyPoll);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [employeeId, scheduleRefresh, refresh]);

  const value = {
    employeeId,
    setActiveEmployee,
    data,
    loading,
    refresh: (opts) => refresh(employeeIdRef.current, opts),
    reconcileSection,
    expandedComponent,
    setExpandedComponent,
    shiftTransition,
    setShiftTransition,
    panelView,
    setPanelView,
    overlayOpen,
    setOverlayOpen,
    railDocked,
    setRailDocked,
    computerIntent,
    requestComputerApp,
    clearComputerIntent,
  };

  return (
    <EmployeeDashboardContext.Provider value={value}>
      {children}
    </EmployeeDashboardContext.Provider>
  );
};

export default EmployeeDashboardContext;
